import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../../lib/utils';
import { 
  RefreshCw, CheckCircle2, AlertCircle, 
  Copy, Image, Search, Sparkles, Activity, Database, Cpu, HardDrive, Check, Save
} from 'lucide-react';

const AdminImageConverter = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [copiedUrl, setCopiedUrl] = useState('');

  // Preference Setting
  const [targetFormat, setTargetFormat] = useState('webp');

  // Result state from bulk scan-and-convert
  const [results, setResults] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/settings`);
      if (res.data.image_converter_settings) {
        setTargetFormat(res.data.image_converter_settings.format || 'webp');
        localStorage.setItem('target_image_format', res.data.image_converter_settings.format || 'webp');
      }
    } catch (e) {
      console.error("Fetch image converter settings failed", e);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');

    try {
      const backendUrl = getBackendUrl();
      await axios.post(`${backendUrl}/api/settings`, {
        key_name: 'image_converter_settings',
        value_data: { format: targetFormat }
      }, { withCredentials: true });
      
      localStorage.setItem('target_image_format', targetFormat);
      setSuccess("Görsel format tercihi başarıyla kaydedildi! Sitedeki tüm görseller artık bu formatta yüklenecektir.");
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error("Save format settings failed", err);
      setError("Format ayarları kaydedilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleScanAndConvert = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/image/scan-and-convert`, {}, {
        withCredentials: true
      });
      
      setResults(res.data);
      setSuccess(`Tüm site başarıyla tarandı ve görseller veritabanına ${targetFormat.toUpperCase()} uzantısı ile kaydedildi!`);
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      console.error("Scan and convert failed", err);
      setError(err.response?.data?.detail || "Görseller taranırken ve dönüştürülürken sunucuda bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0 || !bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const calculateSaving = (original, target) => {
    if (!original || !target || original <= target) return 0;
    const diff = original - target;
    const percent = (diff / original) * 100;
    return Math.max(0, parseFloat(percent.toFixed(1)));
  };

  const copyToClipboard = (text) => {
    const fullUrl = getBackendUrl() + text;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(text);
    setSuccess(`Dosya yolu başarıyla panoya kopyalandı!`);
    setTimeout(() => {
      setSuccess('');
      setCopiedUrl('');
    }, 2000);
  };

  // Filter and Search Logic
  const filteredFiles = results?.converted_files?.filter(file => {
    const query = searchQuery.toLowerCase();
    const filename = file.original.substring(file.original.lastIndexOf('/') + 1);
    const matchesSearch = file.original.toLowerCase().includes(query) || 
                          filename.toLowerCase().includes(query);
    
    if (filterStatus === 'all') return matchesSearch;
    return matchesSearch && file.status === filterStatus;
  }) || [];

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs font-bold text-emerald-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-emerald-50/50">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs font-bold text-rose-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-rose-50/50">
          <AlertCircle size={16} className="text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div>
        <h2 className="text-xl font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
          <Sparkles className="text-blue-600" size={24} />
          Otomatik Site Görsel Tarayıcı ve Dönüştürücü (WebP & AVIF)
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Sitenizdeki tüm resimleri tarayarak seçtiğiniz yeni nesil sıkıştırma formatına dönüştürün, veritabanı yollarını güncelleyin ve disk alanından tasarruf edin.
        </p>
      </div>

      {/* System Status and Scanner Trigger */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
        {/* Trigger Panel (Left / 7 cols) */}
        <div className="lg:col-span-7 space-y-6 flex flex-col justify-between">
          <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm flex-1 flex flex-col justify-between space-y-6">
            <div className="space-y-3">
              <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                <Cpu size={16} className="text-blue-500" />
                Sistem Tarama Kontrolü
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed font-medium">
                Bu araç sitenizin <strong>uploads/</strong> ve <strong>images/</strong> klasöründeki tüm görselleri (JPEG, PNG, GIF) tarayacak; WebP ve AVIF kopyalarını oluşturacaktır. Ardından, veritabanı yollarını <strong>seçmiş olduğunuz formata ({targetFormat.toUpperCase()})</strong> çevirerek güncelleyecektir.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200/40 p-4 rounded-2xl space-y-2.5">
              <div className="flex items-center justify-between text-xs font-bold text-slate-600">
                <span className="flex items-center gap-1.5"><HardDrive size={13} className="text-slate-400" /> Taranacak Klasörler:</span>
                <span className="font-mono bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-700">/api/uploads/, /images/</span>
              </div>
              <div className="flex items-center justify-between text-xs font-bold text-slate-600">
                <span className="flex items-center gap-1.5"><Database size={13} className="text-slate-400" /> Hedef Veritabanı Tabloları:</span>
                <span className="text-[10px] bg-blue-50 px-2 py-0.5 rounded text-blue-700 font-extrabold">gallery, news, settings</span>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={handleScanAndConvert}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2.5 bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-2xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Sistem Taranıyor & Dönüştürülüyor... Lütfen Bekleyin
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    Sitedeki Tüm Görselleri Tara ve Dönüştür
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Settings and Target Format Preference (Right / 5 cols) */}
        <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
          <form onSubmit={handleSaveSettings} className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm space-y-5 flex-1 flex flex-col justify-between">
            <div className="space-y-3">
              <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
                <Save size={16} className="text-blue-500" />
                Dönüşüm Format Ayarı
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                Otomatik yüklenen resimlerin ve toplu dönüştürülen görsellerin veritabanına kaydedileceği ana formatı seçin.
              </p>
            </div>

            {/* Selector Grid */}
            <div className="grid grid-cols-1 gap-3">
              {/* WebP */}
              <div 
                onClick={() => setTargetFormat('webp')}
                className={`p-4 border rounded-2xl cursor-pointer transition flex items-start gap-3 relative overflow-hidden ${
                  targetFormat === 'webp' 
                    ? 'border-blue-500 bg-blue-50/10 shadow-sm' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 shrink-0 ${
                  targetFormat === 'webp' ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                }`}>
                  {targetFormat === 'webp' && <Check size={10} className="text-white" />}
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-slate-800 uppercase flex items-center gap-1.5">
                    WEBP <span className="text-[9px] bg-emerald-50 text-emerald-600 border border-emerald-100 px-1.5 py-0.5 rounded font-extrabold normal-case">Önerilen</span>
                  </h4>
                  <p className="text-[10px] text-slate-500 mt-1 font-medium leading-relaxed">
                    %60-70 sıkıştırma tasarrufu sunar. Tüm modern ve eski web tarayıcılarında %100 uyumluluk sağlar.
                  </p>
                </div>
              </div>

              {/* AVIF */}
              <div 
                onClick={() => setTargetFormat('avif')}
                className={`p-4 border rounded-2xl cursor-pointer transition flex items-start gap-3 relative overflow-hidden ${
                  targetFormat === 'avif' 
                    ? 'border-purple-500 bg-purple-50/10 shadow-sm' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 shrink-0 ${
                  targetFormat === 'avif' ? 'border-purple-500 bg-purple-500' : 'border-slate-300'
                }`}>
                  {targetFormat === 'avif' && <Check size={10} className="text-white" />}
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-slate-800 uppercase flex items-center gap-1.5">
                    AVIF <span className="text-[9px] bg-purple-50 text-purple-600 border border-purple-100 px-1.5 py-0.5 rounded font-extrabold normal-case">Yüksek Tasarruf</span>
                  </h4>
                  <p className="text-[10px] text-slate-500 mt-1 font-medium leading-relaxed">
                    WebP'ye göre %30-40 daha fazla dosya sıkıştırma tasarrufu sunan en son görsel formatıdır.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-1">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-900 text-white py-3 rounded-xl text-xs font-bold transition shadow-sm disabled:opacity-50"
              >
                <Save size={14} /> Tercihi Kaydet
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Results Section */}
      {results && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white border border-slate-200/60 p-4 rounded-2xl shadow-sm text-center space-y-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1"><Image size={11} /> Taranan</p>
              <p className="text-xl font-extrabold text-slate-800">{results.total_files_found} Adet</p>
            </div>
            
            <div className="bg-white border border-slate-200/60 p-4 rounded-2xl shadow-sm text-center space-y-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1"><Database size={11} /> Veritabanı</p>
              <p className="text-xl font-extrabold text-slate-800">{results.database_updates} Satır</p>
            </div>

            <div className="bg-white border border-slate-200/60 p-4 rounded-2xl shadow-sm text-center space-y-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1"><Activity size={11} /> Orijinal Boyut</p>
              <p className="text-xl font-extrabold text-slate-800">{formatSize(results.total_original_size)}</p>
            </div>

            <div className={`p-4 rounded-2xl shadow-sm text-center space-y-1 border ${
              targetFormat === 'webp' ? 'bg-emerald-50/40 border-emerald-100' : 'bg-white border-slate-200/60'
            }`}>
              <p className={`text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
                targetFormat === 'webp' ? 'text-emerald-600' : 'text-slate-400'
              }`}><Sparkles size={11} /> WebP Boyut</p>
              <p className={`text-xl font-extrabold mt-1 ${targetFormat === 'webp' ? 'text-emerald-700' : 'text-slate-800'}`}>{formatSize(results.total_webp_size)}</p>
              <p className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded inline-block ${
                targetFormat === 'webp' ? 'text-emerald-600 bg-emerald-50' : 'text-slate-500 bg-slate-50 border border-slate-100'
              }`}>
                %{calculateSaving(results.total_original_size, results.total_webp_size)} Tasarruf
              </p>
            </div>

            <div className={`p-4 rounded-2xl shadow-sm text-center space-y-1 border ${
              targetFormat === 'avif' ? 'bg-purple-50/40 border-purple-100' : 'bg-white border-slate-200/60'
            }`}>
              <p className={`text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 ${
                targetFormat === 'avif' ? 'text-purple-600' : 'text-slate-400'
              }`}><Sparkles size={11} /> AVIF Boyut</p>
              <p className={`text-xl font-extrabold mt-1 ${targetFormat === 'avif' ? 'text-purple-700' : 'text-slate-800'}`}>{formatSize(results.total_avif_size)}</p>
              <p className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded inline-block ${
                targetFormat === 'avif' ? 'text-purple-600 bg-purple-50' : 'text-slate-500 bg-slate-50 border border-slate-100'
              }`}>
                %{calculateSaving(results.total_original_size, results.total_avif_size)} Tasarruf
              </p>
            </div>
          </div>

          {/* Detailed Image List */}
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">İşlenen Dosyaların Detayları</h3>
              
              {/* Filter Controls */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Görsel veya klasör adı ara..."
                    className="pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>

                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-xl text-xs font-semibold bg-white focus:outline-none"
                >
                  <option value="all">Tüm Dosyalar</option>
                  <option value="converted">Dönüştürülenler</option>
                  <option value="skipped">Atlananlar (Güncel)</option>
                </select>
              </div>
            </div>

            {/* List View */}
            <div className="bg-white border border-slate-200/60 rounded-3xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 font-bold">
                      <th className="p-4">Orijinal Görsel & Boyut</th>
                      <th className="p-4">WebP Versiyonu & Kazanım</th>
                      <th className="p-4">AVIF Versiyonu & Kazanım</th>
                      <th className="p-4 text-center">Durum</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                    {filteredFiles.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="p-8 text-center text-slate-400 italic">
                          Filtreye uygun görsel kaydı bulunamadı.
                        </td>
                      </tr>
                    ) : (
                      filteredFiles.map((file, idx) => {
                        const filename = file.original.substring(file.original.lastIndexOf('/') + 1);
                        return (
                          <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                            {/* Original */}
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                                  <img 
                                    src={getBackendUrl() + file.original} 
                                    alt="Önizleme" 
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                                <div className="max-w-[200px] md:max-w-[300px]">
                                  <p className="font-bold text-slate-800 truncate" title={filename}>{filename}</p>
                                  <p className="text-[10px] text-slate-400 font-semibold">{formatSize(file.original_size)}</p>
                                </div>
                              </div>
                            </td>

                            {/* WebP */}
                            <td className="p-4">
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono text-[10px] bg-blue-50 text-blue-700 px-1 py-0.5 rounded font-extrabold">WEBP</span>
                                  <span className="font-bold text-slate-600">{formatSize(file.webp_size)}</span>
                                  <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                                    %{calculateSaving(file.original_size, file.webp_size)} Küçüldü
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <button 
                                    onClick={() => copyToClipboard(file.webp)}
                                    className="text-[10px] text-slate-400 hover:text-slate-700 flex items-center gap-0.5"
                                  >
                                    <Copy size={10} /> {copiedUrl === file.webp ? "Kopyalandı!" : "Yolu Kopyala"}
                                  </button>
                                </div>
                              </div>
                            </td>

                            {/* AVIF */}
                            <td className="p-4">
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-mono text-[10px] bg-purple-50 text-purple-700 px-1 py-0.5 rounded font-extrabold">AVIF</span>
                                  <span className="font-bold text-slate-600">{formatSize(file.avif_size)}</span>
                                  <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                                    %{calculateSaving(file.original_size, file.avif_size)} Küçüldü
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <button 
                                    onClick={() => copyToClipboard(file.avif)}
                                    className="text-[10px] text-slate-400 hover:text-slate-700 flex items-center gap-0.5"
                                  >
                                    <Copy size={10} /> {copiedUrl === file.avif ? "Kopyalandı!" : "Yolu Kopyala"}
                                  </button>
                                </div>
                              </div>
                            </td>

                            {/* Status */}
                            <td className="p-4 text-center whitespace-nowrap">
                              {file.status === 'converted' ? (
                                <span className="px-2.5 py-1 text-[10px] font-extrabold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">Dönüştürüldü</span>
                              ) : (
                                <span className="px-2.5 py-1 text-[10px] font-extrabold rounded-lg bg-slate-50 text-slate-600 border border-slate-200">Zaten Güncel</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminImageConverter;
